// ABOUTME: Renders the global, area and location actual-versus-planned performance overview.
// ABOUTME: Composes responsive report layouts, filters, navigation and location comments.
import {KpisList, useHierarchy, type PerformanceDescendantWithId} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useQueryParamValue} from '@hconnect/common/hooks'
import {type TimeZone} from '@hconnect/common/types'
import {getSystemTimezone} from '@hconnect/common/utils'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {DashboardCard, useMediaQuery} from '@hm/fundamental'
import {AxiosError} from 'axios'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate, useSearchParams} from 'react-router'

import {CommentDialog, KpiTabs, LocationStatusFilters, PageHeader} from '../components'
import {
  ACTUAL_VS_PLANNED_TAB_PARAM,
  FEATURE_NAMESPACE,
  KEY_TIME_RANGE,
  LOCATION_QUICK_SELECTIONS,
  LOCATION_STATUS_DEFAULT_TIME_RANGE,
  PLANT_QUICK_SELECTIONS
} from '../consts'
import {AcVsPlContainer, MobileAcVsPlContainer} from '../containers'
import {useKpiValue, useLocationStatusDateRange, useLocationStatusPageParams} from '../hooks'
import {useComments} from '../hooks/api/useComments'
import {
  ActualVsPlannedErrorSource,
  ActualVsPlannedRoutes,
  AggregatedKpiData,
  CommentLocationData,
  QuickSelectionId
} from '../types'
import {filterCommentsByLocation, tabIndexFiltered} from '../utils'

type LocationsId = Partial<{
  areaId: string
  regionId: string
  countryId: string
}>

const pathToLocationsId = (path: string): LocationsId => {
  const [areaId, regionId, countryId] = path.split('.')
  return {areaId, regionId, countryId}
}

const isQuickSelectionId = (value: string, list: QuickSelectionId[]) =>
  list.includes(value as QuickSelectionId)

type ActualVsPlannedPageProps = {
  /** KPI dashboard location id (area, region or country); undefined renders the global view */
  locationId?: string
  routes: ActualVsPlannedRoutes
  /** Whether the user may go back to the global overview */
  showBackToGlobal: boolean
  timezone?: TimeZone
  onError?: (error: AxiosError, source: ActualVsPlannedErrorSource) => void
  onGoBackClick?: (route: string) => void
  // true when hosted on the legacy blue surface (hproduce); false (default) on the Suite surface
  inverted?: boolean
}

export const ActualVsPlannedPage: React.FC<ActualVsPlannedPageProps> = ({
  locationId,
  routes,
  showBackToGlobal,
  timezone = getSystemTimezone(),
  onError,
  onGoBackClick,
  inverted = false
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {search} = useLocation()
  const navigate = useNavigate()
  const isMobile = useMediaQuery({breakpoint: 'sm', upTo: true})

  const {timeRangeParam, setTimeRangeParam} = useLocationStatusPageParams()
  const [searchParams] = useSearchParams()
  const {from, to, quickSelectionId} = useLocationStatusDateRange({timezone})
  const dateRange = useMemo(() => ({from, to}), [from, to])

  // The time range param is shared with the plant pages (and persisted in local storage),
  // so reset quick selections this page does not offer, e.g. "today"
  useEffect(() => {
    if (
      isQuickSelectionId(timeRangeParam, PLANT_QUICK_SELECTIONS) &&
      !isQuickSelectionId(timeRangeParam, LOCATION_QUICK_SELECTIONS)
    ) {
      setTimeRangeParam(LOCATION_STATUS_DEFAULT_TIME_RANGE)
      return
    }
    if (!searchParams.has(KEY_TIME_RANGE) && window.localStorage.getItem(KEY_TIME_RANGE) === null) {
      setTimeRangeParam(timeRangeParam)
    }
  }, [timeRangeParam, setTimeRangeParam, searchParams])

  const {data: hierarchy} = useHierarchy()

  const [currentTab = KpisList.ClinkerProduction, setCurrentTab] = useQueryParamValue<KpisList>(
    ACTUAL_VS_PLANNED_TAB_PARAM,
    {
      keepInLocalStorage: false
    }
  )
  const [commentLocationData, setCommentLocationData] = useState<CommentLocationData | undefined>(
    undefined
  )

  const {data: comments = [], error: commentsError} = useComments(
    {
      from: from.toJSON(),
      to: to.toJSON(),
      locationId: locationId || HierarchyNodeType.Global
    },
    {
      select: (data) => data.filter((comment) => comment.topic === currentTab)
    }
  )

  const {data, hasQuarterlyData, isInitialLoading, error, refetch} = useKpiValue({
    currentTab,
    from: from.toJSON(),
    to: to.toJSON(),
    quickSelectionId,
    locationId
  })

  useEffect(() => {
    if (error) onError?.(error, 'kpis')
  }, [error, onError])

  useEffect(() => {
    if (commentsError) onError?.(commentsError, 'comments')
  }, [commentsError, onError])

  const backButtonProps = useMemo(() => {
    const locationsId = data?.locationData?.path
      ? pathToLocationsId(data.locationData.path)
      : undefined
    const areaUrl =
      locationsId?.areaId && (locationsId.countryId || locationsId.regionId)
        ? routes.area(locationsId.areaId)
        : undefined
    if (areaUrl) {
      return {
        targetName: t('goBackLabels.backToArea'),
        target: areaUrl + search
      }
    }
    if (showBackToGlobal && locationId) {
      return {
        targetName: t('goBackLabels.backToGlobal'),
        target: routes.global + search
      }
    }
  }, [showBackToGlobal, data?.locationData?.path, search, locationId, t, routes])

  const handleLocationClick = useCallback(
    ({locationData}: PerformanceDescendantWithId<AggregatedKpiData>) => {
      if (!locationData) return

      if (
        locationData.type === 'area' ||
        locationData.type === 'region' ||
        locationData.type === 'country'
      ) {
        const url =
          locationData.type === 'area'
            ? routes.area(locationData.id)
            : routes.location(locationData.id)
        if (url) void navigate(url + search)
      } else if (locationData.type === 'plant' || (!locationData.type && locationId)) {
        // the plant page has no KPI tabs
        const urlParams = new URLSearchParams(search)
        urlParams.delete(ACTUAL_VS_PLANNED_TAB_PARAM)
        void navigate(`${routes.plant(locationData.id)}?${urlParams.toString()}`)
      }
    },
    [locationId, navigate, routes, search]
  )

  const containerProps = {
    hasQuarterlyData,
    currentTab,
    hideCharts: !tabIndexFiltered.includes(currentTab),
    dateRange,
    setCommentLocationData,
    comments,
    hierarchy,
    onLocationClick: handleLocationClick
  }

  return (
    <PageContainer>
      <div className="pb-4">
        <PageHeader
          filterComponent={
            <LocationStatusFilters
              timeRangeParam={timeRangeParam}
              onTimeRangeParamChange={setTimeRangeParam}
              timezone={timezone}
              inverted={inverted}
            />
          }
          countryCode={data?.locationData?.countryCode}
          title={isInitialLoading ? '' : data?.locationData?.name || t('global.pageTitle')}
          backButtonProps={backButtonProps}
          onGoBackClick={(route) => onGoBackClick?.(route)}
          inverted={inverted}
        />
      </div>

      <DataContentWrapper
        showLoader={isInitialLoading}
        retryFunction={refetch}
        isError={!!error}
        data={data}
      >
        {data && (
          <div data-test-id="ac-vs-pl-page">
            {isMobile ? (
              <>
                <KpiTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />
                <MobileAcVsPlContainer
                  data={data}
                  {...containerProps}
                  getCountryHeaderLink={routes.countryHeader}
                />
              </>
            ) : (
              <DashboardCard data-cy="ac-vs-pl-card">
                <KpiTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />
                <AcVsPlContainer data={data} {...containerProps} />
              </DashboardCard>
            )}
          </div>
        )}
      </DataContentWrapper>

      {commentLocationData && (
        <CommentDialog
          topic={currentTab}
          handleClose={() => setCommentLocationData(undefined)}
          commentLocationData={commentLocationData}
          hierarchy={hierarchy}
          dateRange={dateRange}
          timezone={timezone}
          onError={(createError) => onError?.(createError, 'createComment')}
          comments={
            hierarchy
              ? filterCommentsByLocation(comments, hierarchy, commentLocationData.locationData.id)
              : []
          }
        />
      )}
    </PageContainer>
  )
}
